import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Bot, ChevronDown, FilterX, Rocket, Search } from "lucide-react";
import { useMemo } from "react";
import { Page, PageHeader, useEnvName, useProjectSlug, useRegisterCommands, useWorkspaceSlug } from "@/app";
import {
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  ExplainPanel,
  Input,
  Loading,
  Segmented,
  Select,
  Skeleton,
  toast,
} from "@/design";
import { api, idem, qk, unwrap, useDeployments, useServiceInstances, type Deployment, type DeploymentFilter, type Operation } from "@/lib/api";
import { formatDuration, plural } from "@/lib/format";
import { DeploymentRow } from "./-components/DeploymentRow";
import { groupByDay, isInFlight, previousOf } from "./-components/deploy-utils";

type StatusFilter = "all" | "live" | "failed" | "in_progress" | "replaced" | "rollbacks";
type WhoFilter = "anyone" | "people" | "agents" | "github" | "guv";

interface DeploymentsSearch {
  service?: string;
  status?: StatusFilter;
  who?: WhoFilter;
  scope?: "all";
  q?: string;
}

const STATUS: StatusFilter[] = ["all", "live", "failed", "in_progress", "replaced", "rollbacks"];
const WHO: WhoFilter[] = ["anyone", "people", "agents", "github", "guv"];

export const Route = createFileRoute("/$workspace/$project/deployments/")({
  staticData: { title: "Deployments" },
  validateSearch: (s: Record<string, unknown>): DeploymentsSearch => ({
    service: typeof s.service === "string" && s.service ? s.service : undefined,
    status: STATUS.includes(s.status as StatusFilter) && s.status !== "all" ? (s.status as StatusFilter) : undefined,
    who: WHO.includes(s.who as WhoFilter) && s.who !== "anyone" ? (s.who as WhoFilter) : undefined,
    scope: s.scope === "all" ? "all" : undefined,
    q: typeof s.q === "string" && s.q ? s.q : undefined,
  }),
  component: DeploymentsScreen,
});

const whoToActor: Record<Exclude<WhoFilter, "anyone">, DeploymentFilter["actor_type"]> = { people: "user", agents: "agent", github: "github", guv: "system" };

function matchesStatus(d: Deployment, f: StatusFilter): boolean {
  switch (f) {
    case "all":
      return true;
    case "live":
      return d.status === "active";
    case "failed":
      return d.status === "failed" || d.status === "crashed";
    case "in_progress":
      return isInFlight(d.status);
    case "replaced":
      return d.status === "superseded" || d.status === "removed";
    case "rollbacks":
      return d.trigger.kind === "rollback";
  }
}

function DeploymentsScreen() {
  const project = useProjectSlug();
  const env = useEnvName();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<DeploymentsSearch>) => void navigate({ search: (s) => ({ ...s, ...patch }), replace: true });

  const filter: DeploymentFilter = {
    limit: 100,
    ...(search.scope === "all" ? {} : { environment: env }),
    ...(search.service ? { service: search.service } : {}),
    ...(search.who && search.who !== "anyone" ? { actor_type: whoToActor[search.who] } : {}),
  };
  const deployments = useDeployments(project, filter);
  const instances = useServiceInstances(project, env);
  const all = deployments.data?.data ?? [];
  const q = search.q?.toLowerCase();
  const shown = all.filter(
    (d) =>
      matchesStatus(d, search.status ?? "all") &&
      (!q || [d.source?.commit?.message, d.source?.commit?.sha, d.service_name, d.trigger.actor.display, d.id].some((x) => x?.toLowerCase().includes(q))),
  );
  const groups = useMemo(() => groupByDay(shown), [shown]);
  const filtered = !!(search.service || search.status || search.who || search.q);
  const serviceNames = useMemo(() => [...new Set((instances.data?.data ?? []).map((i) => i.name))], [instances.data]);

  return (
    <Page>
      <PageHeader
        title="Deployments"
        description={
          <>
            Every build, deploy and rollback{search.scope === "all" ? " in every environment" : <> in <strong className="font-medium text-text">{env}</strong></>}, and who or which agent started it.
          </>
        }
        actions={<DeployNow services={serviceNames} />}
      />

      <WeekSummary deployments={all} loading={deployments.isPending} />

      <div className="mb-4 mt-6 flex flex-col gap-3 lg:flex-row lg:items-center">
        <Input
          leading={<Search />}
          placeholder="Search commit, SHA, person or agent"
          aria-label="Search deployments"
          value={search.q ?? ""}
          onChange={(e) => set({ q: e.target.value || undefined })}
          className="lg:max-w-xs"
        />
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
          <Select
            size="sm"
            aria-label="Service"
            className="sm:w-40"
            value={search.service ?? "all"}
            onValueChange={(v) => set({ service: v === "all" ? undefined : v })}
            options={[{ value: "all", label: "All services" }, ...serviceNames.map((n) => ({ value: n, label: n }))]}
          />
          <Select
            size="sm"
            aria-label="Status"
            className="sm:w-40"
            value={search.status ?? "all"}
            onValueChange={(v) => set({ status: v === "all" ? undefined : (v as StatusFilter) })}
            options={[
              { value: "all", label: "Any status" },
              { value: "live", label: "Live now" },
              { value: "failed", label: "Failed" },
              { value: "in_progress", label: "In progress" },
              { value: "replaced", label: "Replaced" },
              { value: "rollbacks", label: "Rollbacks" },
            ]}
          />
          <Select
            size="sm"
            aria-label="Who started it"
            className="sm:w-44"
            value={search.who ?? "anyone"}
            onValueChange={(v) => set({ who: v === "anyone" ? undefined : (v as WhoFilter) })}
            options={[
              { value: "anyone", label: "Started by anyone" },
              { value: "people", label: "People" },
              { value: "agents", label: "AI agents", icon: <Bot /> },
              { value: "github", label: "GitHub pushes" },
              { value: "guv", label: "Guv (automatic)" },
            ]}
          />
          <Segmented
            aria-label="Environments"
            size="sm"
            value={search.scope === "all" ? "all" : "this"}
            onValueChange={(v) => set({ scope: v === "all" ? "all" : undefined })}
            options={[
              { value: "this", label: env },
              { value: "all", label: "All environments" },
            ]}
            className="col-span-2 justify-self-start"
          />
        </div>
        {filtered && (
          <Button variant="ghost" size="sm" icon={<FilterX />} onClick={() => set({ service: undefined, status: undefined, who: undefined, q: undefined })} className="lg:ml-auto">
            Clear filters
          </Button>
        )}
      </div>

      {deployments.isPending ? (
        <Loading label="Loading deployments">
          <Card className="divide-y divide-border">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-3 px-5 py-4">
                <Skeleton className="size-7 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </Card>
        </Loading>
      ) : deployments.isError ? (
        <ErrorState error={deployments.error} onRetry={() => deployments.refetch()} />
      ) : shown.length === 0 ? (
        <Card>
          {filtered ? (
            <EmptyState icon={<FilterX />} title="No deploys match these filters" description="Try another status or person, or clear the filters to see everything." actions={<Button onClick={() => set({ service: undefined, status: undefined, who: undefined, q: undefined })}>Clear filters</Button>} />
          ) : (
            <EmptyState
              icon={<Rocket />}
              title={`Nothing deployed to ${env} yet`}
              description="Push to your connected branch, run guv deploy, or ask your AI agent to deploy. Every deploy will appear here with who started it and how long it took."
            />
          )}
        </Card>
      ) : (
        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.label} aria-labelledby={`day-${g.label}`}>
              <h2 id={`day-${g.label}`} className="mb-2 flex items-baseline gap-2 text-sm font-semibold text-text">
                {g.label}
                <span className="font-normal text-subtle">{plural(g.items.length, "deploy")}</span>
              </h2>
              <Card>
                <ul className="divide-y divide-border">
                  {g.items.map((d) => (
                    <DeploymentRow key={d.id} d={d} previous={previousOf(all, d)} showContext />
                  ))}
                </ul>
              </Card>
            </section>
          ))}
          {deployments.data?.has_more && <p className="text-center text-sm text-muted">Showing the latest 100. Use the CLI for older ones: <code className="font-mono">guv deployments list --before …</code></p>}
        </div>
      )}
    </Page>
  );
}

function WeekSummary({ deployments, loading }: { deployments: Deployment[]; loading: boolean }) {
  const week = deployments.filter((d) => Date.now() - Date.parse(d.created_at) < 7 * 86400_000);
  const failed = week.filter((d) => d.status === "failed" || d.status === "crashed").length;
  const agents = week.filter((d) => d.trigger.actor.type === "agent").length;
  const durations = week.map((d) => d.duration_ms).filter((x): x is number => x != null).sort((a, b) => a - b);
  const median = durations.length ? durations[Math.floor(durations.length / 2)]! : null;
  const items = [
    { label: "Deploys this week", value: String(week.length) },
    { label: "Failed", value: String(failed), hint: failed ? "A failed deploy never replaces what's live." : "None this week." },
    { label: "Typical time to live", value: median ? formatDuration(median) : "–" },
    { label: "Started by AI agents", value: String(agents) },
  ];
  return (
    <Card className="grid grid-cols-2 divide-border sm:grid-cols-4 sm:divide-x">
      {items.map((it, i) => (
        <div key={it.label} className={i < 2 ? "border-b border-border px-5 py-4 sm:border-b-0" : "px-5 py-4"}>
          <div className="flex items-center gap-1 text-sm text-muted">
            {it.label}
            {it.label === "Failed" && (
              <ExplainPanel title="Failed deploys never take your app down">
                <p>Guv only switches traffic once the new version passes its health check. If it fails, your <strong>previous version keeps serving</strong>, and we explain what went wrong with a one-click fix.</p>
              </ExplainPanel>
            )}
          </div>
          <div className="mt-0.5 font-display text-2xl font-semibold tabular text-text">{loading ? <Skeleton className="mt-1 h-6 w-10" /> : it.value}</div>
        </div>
      ))}
    </Card>
  );
}

function DeployNow({ services }: { services: string[] }) {
  const project = useProjectSlug();
  const workspace = useWorkspaceSlug();
  const env = useEnvName();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const deploy = useMutation({
    mutationFn: (service: string) =>
      unwrap(
        api.POST("/v1/projects/{project}/environments/{environment}/services/{service}/deployments", {
          params: { path: { project, environment: env, service }, header: idem() },
          body: { message: "Deployed from the dashboard", clear_build_cache: false },
        }),
      ) as Promise<Operation>,
    onSuccess: (op, service) => {
      void qc.invalidateQueries({ queryKey: qk.project(project) });
      toast.success(`Deploying ${service} to ${env}`, { description: "We'll switch traffic once it passes its health check." });
      if (op.resource?.type === "deployment") {
        void navigate({ to: "/$workspace/$project/deployments/$deploymentId", params: { workspace, project, deploymentId: op.resource.id }, search: ((s: Record<string, unknown>) => s) as never });
      }
    },
    onError: (e) => toast.error(e),
  });
  useRegisterCommands(
    "Deployments",
    services.map((s) => ({ id: `deploy-${s}`, label: `Deploy ${s} to ${env}`, icon: <Rocket />, run: () => deploy.mutate(s) })),
    [services.join(","), env],
  );
  if (!services.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="primary" icon={<Rocket />} iconRight={<ChevronDown />} loading={deploy.isPending}>
          Deploy now
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Deploy the latest commit to {env}</DropdownMenuLabel>
        {services.map((s) => (
          <DropdownMenuItem key={s} icon={<Rocket />} onSelect={() => deploy.mutate(s)}>
            {s}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
